import { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classes from './transactionMetaPopover.module.scss'

export interface ITransactionMeta {
    is_offer_accepted?: boolean;
    is_privacy_policy_accepted?: boolean;
    is_personal_data_consent_given?: boolean;
}

interface IProps {
    meta?: ITransactionMeta;
}

interface IPosition {
    top: number;
    left: number;
}

const metaLabels: Array<{key: keyof ITransactionMeta; label: string}> = [
    {key: 'is_offer_accepted', label: 'Оферта'},
    {key: 'is_privacy_policy_accepted', label: 'Политика конфиденциальности'},
    {key: 'is_personal_data_consent_given', label: 'Согласие на обработку персональных данных'},
]

export const TransactionMetaPopover: FC<IProps> = ({meta}) => {
    const buttonRef = useRef<HTMLButtonElement>(null)
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [isOpen, setIsOpen] = useState(false)
    const [position, setPosition] = useState<IPosition>({top: 0, left: 0})

    const updatePosition = () => {
        const rect = buttonRef.current?.getBoundingClientRect()
        if(!rect) return

        setPosition({
            top: rect.bottom + 8,
            left: Math.max(12, Math.min(rect.left, window.innerWidth - 332)),
        })
    }

    const clearCloseTimeout = () => {
        if(closeTimeoutRef.current){
            clearTimeout(closeTimeoutRef.current)
            closeTimeoutRef.current = null
        }
    }

    const open = () => {
        clearCloseTimeout()
        updatePosition()
        setIsOpen(true)
    }

    const closeWithDelay = () => {
        clearCloseTimeout()
        closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 100)
    }

    useEffect(() => {
        if(!isOpen) return

        const onViewportChange = () => updatePosition()
        window.addEventListener('resize', onViewportChange)
        window.addEventListener('scroll', onViewportChange, true)

        return () => {
            window.removeEventListener('resize', onViewportChange)
            window.removeEventListener('scroll', onViewportChange, true)
        }
    }, [isOpen])

    useEffect(() => () => clearCloseTimeout(), [])

    const popup = isOpen && typeof document !== 'undefined'
        ? createPortal(
            <section
                className={classes.popup}
                style={{top: position.top, left: position.left}}
                role="tooltip"
                onMouseEnter={open}
                onMouseLeave={closeWithDelay}
            >
                <section className={classes.title}>Дополнительная информация</section>
                <ul className={classes.list}>
                    {metaLabels.map(item => {
                        const value = meta?.[item.key]
                        return <li key={item.key}>
                            <span>{item.label}</span>
                            <span className={value === true ? classes.yes : value === false ? classes.no : classes.empty}>
                                {value === true ? 'Да' : value === false ? 'Нет' : 'Не указано'}
                            </span>
                        </li>
                    })}
                </ul>
            </section>,
            document.body,
        )
        : null

    return <>
        <button
            ref={buttonRef}
            type="button"
            className={classes.button}
            aria-expanded={isOpen}
            onMouseEnter={open}
            onMouseLeave={closeWithDelay}
            onFocus={open}
            onBlur={closeWithDelay}
            onClick={open}
        >
            Посмотреть
        </button>
        {popup}
    </>
}
